import { useCallback, useRef, useState } from "react";
import type { Profile } from "./auth";

export interface Turn {
  question: string;
  answer: string | null;
  error: string | null;
  groundedIn?: { symbols: string[]; themes: string[] };
}

/** What the dock knows about the page a question was asked from. */
export interface PageContext {
  route: string;
  symbol?: string;
}

interface StoredMessage {
  role: "user" | "assistant";
  content: string;
  groundedIn?: { symbols: string[]; themes: string[] };
}

export type HistoryState = "idle" | "loading" | "ready" | "not_approved";

/**
 * Conversation state for the chat dock.
 *
 * Lives outside the widget so the fetch and error-classification logic is
 * testable apart from the UI. The server owns the transcript: `send` posts
 * only the new question plus page context, and `activate` pulls whatever the
 * server has stored the first time the panel opens for a signed-in user.
 */
export function useChat(profile: Profile | null) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [historyState, setHistoryState] = useState<HistoryState>("idle");
  const loadedFor = useRef<string | null>(null);

  /** Loads stored history once per signed-in identity. Call on panel open. */
  const activate = useCallback(async () => {
    if (!profile) return;
    if (loadedFor.current === profile.userId) return;
    loadedFor.current = profile.userId;
    setHistoryState("loading");

    try {
      const res = await fetch("/api/chat");

      // Signed in but not on the allowlist — worth knowing before a question
      // is typed, so the dock can say so up front.
      if (res.status === 403) {
        setHistoryState("not_approved");
        return;
      }

      const contentType = res.headers.get("content-type") ?? "";
      if (!res.ok || !contentType.includes("application/json")) {
        // A lapsed session or a transient failure: start empty rather than
        // block the panel. Sending a question will surface the real state.
        setHistoryState("ready");
        return;
      }

      const body = (await res.json()) as { messages?: StoredMessage[] };
      setTurns(toTurns(body.messages ?? []));
      setHistoryState("ready");
    } catch {
      setHistoryState("ready");
    }
  }, [profile]);

  const send = useCallback(
    async (question: string, pageContext: PageContext) => {
      const trimmed = question.trim();
      if (!trimmed || busy) return;

      setBusy(true);
      setTurns((t) => [...t, { question: trimmed, answer: null, error: null }]);

      const patchLast = (patch: Partial<Turn>) =>
        setTurns((t) => t.map((turn, i) => (i === t.length - 1 ? { ...turn, ...patch } : turn)));

      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ question: trimmed, pageContext }),
        });

        const contentType = res.headers.get("content-type") ?? "";
        const isJson = contentType.includes("application/json");

        // A 401, or a *successful* response carrying a web page. The latter is
        // what a lapsed session looks like: the platform redirects to the login
        // page and fetch() follows transparently, so the status is 200 and the
        // body is HTML.
        //
        // The success check is the load-bearing half. Without it the condition
        // was "401 or not JSON", which swept in every server fault too — a 500
        // told people to sign in again, they did, the next question failed
        // identically, and nothing pointed at the actual bug.
        if (res.status === 401 || (res.ok && !isJson)) {
          patchLast({ error: "SIGN_IN_REQUIRED" });
          return;
        }

        // Signed in, but not on the allowlist. A distinct state from being
        // signed out, and signing in again will not fix it — saying "sign in"
        // here would send someone round a loop that cannot terminate.
        if (res.status === 403) {
          patchLast({ error: "NOT_APPROVED" });
          return;
        }

        // An error with no structured body to quote. Say plainly whose fault it
        // is rather than inventing a cause the reader might act on.
        if (!isJson) {
          patchLast({
            error: `The assistant failed on that question (${res.status}). This is a bug, not something you did.`,
          });
          return;
        }

        const body = (await res.json()) as {
          answer?: string;
          error?: string;
          groundedIn?: { symbols: string[]; themes: string[] };
        };

        patchLast({
          answer: body.answer ?? null,
          error: body.answer ? null : (body.error ?? `Request failed (${res.status})`),
          ...(body.groundedIn ? { groundedIn: body.groundedIn } : {}),
        });
      } catch (err) {
        patchLast({ error: err instanceof Error ? err.message : String(err) });
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  /** Starts a new conversation, here and on the server. */
  const reset = useCallback(async () => {
    setTurns([]);
    try {
      await fetch("/api/chat", { method: "DELETE" });
    } catch {
      // The local slate is already clean; a failed delete only means the old
      // transcript may reappear on the next load.
    }
  }, []);

  return { turns, busy, historyState, activate, send, reset };
}

/** Pairs a stored message list back into question/answer turns. */
function toTurns(messages: StoredMessage[]): Turn[] {
  const turns: Turn[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      turns.push({ question: m.content, answer: null, error: null });
      continue;
    }
    const last = turns[turns.length - 1];
    if (last && last.answer === null) {
      last.answer = m.content;
      if (m.groundedIn) last.groundedIn = m.groundedIn;
    }
  }
  // A trailing unanswered question (an interrupted turn) is dropped rather
  // than shown as eternally "Thinking…".
  return turns.filter((t) => t.answer !== null);
}
